/**
 * ChatGPTConnectModal — Guided flow for connecting a ChatGPT account.
 *
 * Steps:
 * 1. Open chatgpt.com/api/auth/session in a new tab
 * 2. Copy the accessToken from the JSON
 * 3. Paste it here and click Connect
 *
 * @module @humanizer/studio/components/import/ChatGPTConnectModal
 */

import React, { useState, useCallback } from 'react';
import { useApi } from '../../contexts/ApiContext';

interface ChatGPTConnectModalProps {
  onClose: () => void;
  onConnected: () => void;
}

export function ChatGPTConnectModal({ onClose, onConnected }: ChatGPTConnectModalProps): React.ReactElement {
  const api = useApi();
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleConnect = useCallback(async () => {
    if (!token.trim()) return;

    setStatus('saving');
    setErrorMsg('');

    try {
      await api.chatgptImport.saveToken(token.trim());
      setStatus('success');
      setTimeout(() => {
        onConnected();
        onClose();
      }, 1200);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save token');
    }
  }, [api.chatgptImport, token, onConnected, onClose]);

  return (
    <div className="chatgpt-connect-modal__overlay" onClick={onClose} role="presentation">
      <div className="chatgpt-connect-modal" onClick={(e) => e.stopPropagation()} role="presentation">
        <div className="chatgpt-connect-modal__header">
          <h3 className="chatgpt-connect-modal__title">Connect ChatGPT</h3>
          <button className="chatgpt-connect-modal__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {status === 'success' ? (
          <div className="chatgpt-connect-modal__success">
            <div className="chatgpt-connect-modal__success-icon">&#10003;</div>
            <p>Connected successfully!</p>
          </div>
        ) : (
          <div className="chatgpt-connect-modal__body">
            <div className="chatgpt-connect-modal__step">
              <span className="chatgpt-connect-modal__step-num">1</span>
              <div className="chatgpt-connect-modal__step-content">
                <p>Open the ChatGPT session endpoint in a new tab:</p>
                <button
                  className="chatgpt-connect-modal__link-btn"
                  onClick={() => window.open('https://chatgpt.com/api/auth/session', '_blank')}
                >
                  Open chatgpt.com/api/auth/session
                </button>
              </div>
            </div>

            <div className="chatgpt-connect-modal__step">
              <span className="chatgpt-connect-modal__step-num">2</span>
              <div className="chatgpt-connect-modal__step-content">
                <p>
                  Copy the <code className="chatgpt-connect-modal__code">accessToken</code> value
                  from the JSON. It starts with <code className="chatgpt-connect-modal__code">eyJ...</code>
                </p>
              </div>
            </div>

            <div className="chatgpt-connect-modal__step">
              <span className="chatgpt-connect-modal__step-num">3</span>
              <div className="chatgpt-connect-modal__step-content">
                <p>Paste the token below:</p>
                <input
                  type="password"
                  className="chatgpt-connect-modal__input"
                  placeholder="eyJ..."
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {errorMsg && (
              <div className="chatgpt-connect-modal__error" role="alert">{errorMsg}</div>
            )}

            <div className="chatgpt-connect-modal__actions">
              <button
                className="chatgpt-connect-modal__cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="chatgpt-connect-modal__connect-btn"
                onClick={() => void handleConnect()}
                disabled={!token.trim() || status === 'saving'}
              >
                {status === 'saving' ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatGPTConnectModal;
